import React, { createContext, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { LDProvider, useLDClient } from 'launchdarkly-react-client-sdk';
import { BrandContext } from './brandContext';

const LD_FLAG_DEFAULTS = {
  'ops-pay-now-enabled': true,
  'config-legacy-payment-mode-enabled': false,
  'config-notification-preferences-enabled': true,
  'config-kontent-ai-preview-mode-enabled': false,
  'ops-lite-mode-enabled': false,
  'config-live-vet-enabled': true,
  'config-excess-enabled': true,
  'config-whats-covered-enabled': true,
  'config-lec-enabled': false,
  'config-short-birth-date-enabled': false,
  'config-dual-portal-enabled': true,
  'temp-instalment-processing-enabled': false,
  'temp-v3-mw-pen-test-remediation-endpoints-enabled': true,
  'config-fallback-mode-middleware-api-enabled': false,
  'config-renewal-hub-enabled': false,
  'temp-create-portal-user-token-endpoint': true
};

const LDReadyContext = createContext(undefined);

export const useLDReady = () => {
  const value = useContext(LDReadyContext);
  if (value === undefined) {
    throw new Error('useLDReady must be used inside LaunchDarklyProvider');
  }
  return value;
};

const LDReadyTracker = ({ children }) => {
  const ldClient = useLDClient();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!ldClient) {
      // eslint-disable-next-line no-console
      console.warn('LaunchDarkly client not available. Rendering with default flag values.');
      setIsReady(true);
      return undefined;
    }
    let cancelled = false;
    const timeout = setTimeout(() => {
      if (!cancelled) setIsReady(true);
    }, 5000);
    ldClient
      .waitForInitialization()
      .catch(() => {})
      .finally(() => {
        if (cancelled) return;
        clearTimeout(timeout);
        setIsReady(true);
      });
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [ldClient]);

  return <LDReadyContext.Provider value={isReady}>{children}</LDReadyContext.Provider>;
};

LDReadyTracker.propTypes = {
  children: PropTypes.node.isRequired
};

export const LaunchDarklyProvider = ({ children }) => {
  const {
    activeBrandContext: {
      brand: { partner }
    }
  } = useContext(BrandContext);

  const clientSideID =
    typeof process.env.LAUNCHDARKLY_CLIENT_SIDE_ID === 'string' ? process.env.LAUNCHDARKLY_CLIENT_SIDE_ID.trim() : '';
  const partnerKey = typeof partner === 'string' ? partner.trim().toLowerCase() : '';
  const hasValidLaunchDarklyConfig = Boolean(clientSideID) && Boolean(partnerKey);

  if (!hasValidLaunchDarklyConfig) {
    // eslint-disable-next-line no-console
    console.error(
      'LaunchDarklyProvider: missing or invalid LaunchDarkly configuration; rendering LaunchDarkly in offline mode with default flag values.',
      {
        hasClientSideID: Boolean(clientSideID),
        hasPartnerKey: Boolean(partnerKey)
      }
    );
  }

  const ldClientSideID = hasValidLaunchDarklyConfig ? clientSideID : 'offline-client-side-id';
  const ldContext = { kind: 'partner', key: partnerKey || 'offline-partner' };
  const ldProviderOptions = hasValidLaunchDarklyConfig
    ? undefined
    : {
        offline: true,
        bootstrap: LD_FLAG_DEFAULTS
      };

  return (
    <LDProvider
      clientSideID={ldClientSideID}
      context={ldContext}
      flags={LD_FLAG_DEFAULTS}
      options={ldProviderOptions}
    >
      <LDReadyTracker>{children}</LDReadyTracker>
    </LDProvider>
  );
};

LaunchDarklyProvider.propTypes = {
  children: PropTypes.node.isRequired
};
